import React, { useCallback, useState } from "react";
import axios from "axios";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import * as Constants from "../api/constants";
import { resolveCmsAssetUrl } from "../utils/cmsAssetUrl";

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/** Home hero videos + keyed images saved on the Home slice of CMS. Uploads reuse AdminSitePageMedia APIs. */
export default function AdminSitePageMediaPanel({ homeDraft, setHomeDraft, showBanner, onSave, onReload }) {
  const [busyKey, setBusyKey] = useState(null);

  const uploadAndSet = useCallback(
    async (key, file, setter) => {
      if (!file?.type?.startsWith("image/")) {
        showBanner("warning", "Choose an image file.");
        return;
      }
      setBusyKey(key);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(Constants.ADMIN_SITE_PAGE_MEDIA_UPLOAD, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const path = res.data?.url;
        if (typeof path === "string" && path.length) {
          setter(path);
          showBanner("success", "Uploaded — click Save to persist in CMS and server file.");
        } else {
          showBanner("warning", "Unexpected response — paste a URL or check server logs.");
        }
      } catch (e) {
        try {
          const url = await readFileAsDataUrl(file);
          setter(url);
          showBanner(
            "warning",
            "Server upload failed — embedded as data URL (small images only)."
          );
        } catch {
          showBanner(
            "danger",
            "Upload failed. Is Spring Boot running? Paste a full https URL instead."
          );
        }
      } finally {
        setBusyKey(null);
      }
    },
    [showBanner]
  );

  const uploadVideoAndSet = useCallback(
    async (key, file, setter) => {
      const name = String(file?.name || "");
      const okType = Boolean(file?.type?.startsWith("video/"));
      const okExt = /\.(mp4|webm|mov)$/i.test(name);
      if (!file || !(okType || okExt)) {
        showBanner("warning", "Choose a video file (.mp4, .webm, or .mov).");
        return;
      }
      setBusyKey(key);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(Constants.ADMIN_SITE_PAGE_MEDIA_UPLOAD_VIDEO, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const path = res.data?.url;
        if (typeof path === "string" && path.length) {
          setter(path);
          showBanner("success", "Video uploaded — click Save to persist in CMS.");
        } else {
          showBanner("warning", "Unexpected response — paste a URL.");
        }
      } catch {
        showBanner(
          "danger",
          "Video upload failed. Check multipart limits and server logs — or paste an https URL."
        );
      } finally {
        setBusyKey(null);
      }
    },
    [showBanner]
  );

  const setCelebrationImage = (index, url) => {
    setHomeDraft((prev) => {
      const next = [...(prev.celebrations || [])];
      next[index] = { ...next[index], imageUrl: url };
      return { ...prev, celebrations: next };
    });
  };

  const setGalleryImage = (index, url) => {
    setHomeDraft((prev) => {
      const next = [...(prev.gallery || [])];
      next[index] = { ...next[index], imageUrl: url };
      return { ...prev, gallery: next };
    });
  };

  const setMarketingImage = (index, url) => {
    setHomeDraft((prev) => {
      const base = [...(prev.marketingTheatres || [])];
      base[index] = { ...base[index], imageUrl: url };
      return { ...prev, marketingTheatres: base };
    });
  };

  const celebrationLabels = [
    "Celebration card · Birthday Party",
    "Celebration card · Anniversary",
    "Celebration card · Surprise Party",
    "Celebration card · Party & Events",
  ];

  const galleryLabels = [
    "Gallery · Luxury Birthday Setup",
    "Gallery · Balloon Décor",
    "Gallery · Anniversary Moments",
    "Gallery · Party Celebration",
    "Gallery · Special Surprise",
  ];

  const theatres = homeDraft.marketingTheatres || [];

  const slotCard = ({
    slotKey,
    label,
    value,
    onUrlChange,
    onUploadFile,
    helpText,
  }) => {
    const busy = busyKey === slotKey;
    const preview = resolveCmsAssetUrl(value || "");
    return (
      <Card key={slotKey} className="bg-dark bg-opacity-25 border-secondary mb-3 text-light">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap gap-3 align-items-start justify-content-between">
            <div className="flex-grow-1 min-w-0" style={{ maxWidth: "100%" }}>
              <div className="small fw-bold text-secondary mb-1">{label}</div>
              <Form.Control
                className="mb-2"
                size="sm"
                value={value || ""}
                placeholder="/images/cms-defaults/… or paste https URL"
                onChange={(e) => onUrlChange(e.target.value)}
              />
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline-light"
                  disabled={busy}
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = () => {
                      const f = input.files?.[0];
                      onUploadFile(f);
                    };
                    input.click();
                  }}
                >
                  {busy ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1 align-middle" />
                      Uploading…
                    </>
                  ) : (
                    "Upload to server"
                  )}
                </Button>
                <span className="text-secondary small">{helpText}</span>
              </div>
              {preview ? (
                <div className="admin-site-image-preview mt-2">
                  <img src={preview} alt="" className="rounded border border-secondary w-100" />
                </div>
              ) : null}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const slotVideoCard = ({
    slotKey,
    label,
    value,
    onUrlChange,
    onUploadFile,
    helpText,
  }) => {
    const busy = busyKey === slotKey;
    const src = resolveCmsAssetUrl(value || "");
    return (
      <Card key={slotKey} className="bg-dark bg-opacity-25 border-secondary mb-3 text-light">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap gap-3 align-items-start justify-content-between">
            <div className="flex-grow-1 min-w-0" style={{ maxWidth: "100%" }}>
              <div className="small fw-bold text-secondary mb-1">{label}</div>
              <Form.Control
                className="mb-2"
                size="sm"
                value={value || ""}
                placeholder="/videos/cms-defaults/… or paste https URL"
                onChange={(e) => onUrlChange(e.target.value)}
              />
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline-light"
                  disabled={busy}
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "video/*,.mp4,.webm,.mov";
                    input.onchange = () => {
                      const f = input.files?.[0];
                      onUploadFile(f);
                    };
                    input.click();
                  }}
                >
                  {busy ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1 align-middle" />
                      Uploading…
                    </>
                  ) : (
                    "Upload video to server"
                  )}
                </Button>
                <span className="text-secondary small">{helpText}</span>
              </div>
              {src ? (
                <div className="admin-site-video-preview mt-2">
                  <video
                    key={src}
                    src={src}
                    className="w-100 rounded border border-secondary"
                    muted
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="pb-admin-site-images">
      <p className="text-secondary small mb-3">
        Hero videos and carousel images write into the Home slice of CMS. Images go to{" "}
        <code className="text-info">partyboss-data</code> via <code className="text-info">/adminSitePageMedia/upload</code>
        ; videos via <code className="text-info">upload-video</code>. Paste URLs if you prefer a CDN.
      </p>

      <div className="d-flex flex-wrap gap-2 mb-4">
        <Button variant="success" size="sm" type="button" onClick={() => onSave?.()}>
          Save page media (home slice)
        </Button>
        {onReload ? (
          <Button variant="outline-secondary" size="sm" type="button" onClick={onReload}>
            Reload from saved CMS
          </Button>
        ) : null}
      </div>

      <h5 className="text-white mb-3">Home · hero videos</h5>
      {slotVideoCard({
        slotKey: "hero-bg-video",
        label: "Full-width background video (behind hero headline)",
        value: homeDraft.hero?.backgroundVideoUrl || "",
        onUrlChange: (v) =>
          setHomeDraft((prev) => ({
            ...prev,
            hero: { ...prev.hero, backgroundVideoUrl: v },
          })),
        onUploadFile: (f) =>
          uploadVideoAndSet("hero-bg-video", f, (u) =>
            setHomeDraft((prev) => ({
              ...prev,
              hero: { ...prev.hero, backgroundVideoUrl: u },
            }))
          ),
        helpText: "Muted loop behind the headline.",
      })}
      {slotVideoCard({
        slotKey: "hero-card-video",
        label: "Hero card fallback video",
        value: homeDraft.hero?.cardVideoUrl || "",
        onUrlChange: (v) =>
          setHomeDraft((prev) => ({
            ...prev,
            hero: { ...prev.hero, cardVideoUrl: v },
          })),
        onUploadFile: (f) =>
          uploadVideoAndSet("hero-card-video", f, (u) =>
            setHomeDraft((prev) => ({
              ...prev,
              hero: { ...prev.hero, cardVideoUrl: u },
            }))
          ),
        helpText: "Shows in the hero card when no slideshow picks apply.",
      })}

      <h5 className="text-white mt-4 mb-3">Celebrations &amp; Home · four cards</h5>
      {celebrationLabels.map((label, i) =>
        slotCard({
          slotKey: `celebration-${i}`,
          label,
          value: homeDraft.celebrations?.[i]?.imageUrl || "",
          onUrlChange: (v) => setCelebrationImage(i, v),
          onUploadFile: (f) =>
            uploadAndSet(`celebration-${i}`, f, (u) => setCelebrationImage(i, u)),
          helpText: "Also shown on /celebrations.",
        })
      )}

      <h5 className="text-white mt-4 mb-3">Food page &amp; Home · food block</h5>
      {slotCard({
        slotKey: "food",
        label: "Food section hero image",
        value: homeDraft.foodSection?.imageUrl || "",
        onUrlChange: (v) =>
          setHomeDraft((prev) => ({
            ...prev,
            foodSection: { ...prev.foodSection, imageUrl: v },
          })),
        onUploadFile: (f) =>
          uploadAndSet("food", f, (u) =>
            setHomeDraft((prev) => ({
              ...prev,
              foodSection: { ...prev.foodSection, imageUrl: u },
            }))
          ),
        helpText: "/food and Home",
      })}

      <h5 className="text-white mt-4 mb-3">Gallery strip</h5>
      {galleryLabels.map((label, i) =>
        slotCard({
          slotKey: `gallery-${i}`,
          label,
          value: homeDraft.gallery?.[i]?.imageUrl || "",
          onUrlChange: (v) => setGalleryImage(i, v),
          onUploadFile: (f) => uploadAndSet(`gallery-${i}`, f, (u) => setGalleryImage(i, u)),
          helpText: "/gallery",
        })
      )}

      <h5 className="text-white mt-4 mb-3">Marketing sample theatres · images</h5>
      {theatres.length === 0 ? (
        <p className="text-secondary small">Add marketing theatres under Home → Sections & lists.</p>
      ) : null}
      {theatres.map((row, i) =>
        slotCard({
          slotKey: `marketing-${row.key || i}`,
          label: `Marketing theatre · ${row.name || row.key || `Row ${i + 1}`}`,
          value: row?.imageUrl || "",
          onUrlChange: (v) => setMarketingImage(i, v),
          onUploadFile: (f) =>
            uploadAndSet(`marketing-${row.key || i}`, f, (u) => setMarketingImage(i, u)),
          helpText: "Demo cards on Home",
        })
      )}

      <h5 className="text-white mt-4 mb-3">Home · final CTA background</h5>
      {slotCard({
        slotKey: "finalCtaBg",
        label: "Full-width banner behind closing CTA",
        value: homeDraft.finalCta?.bgImageUrl || "",
        onUrlChange: (v) =>
          setHomeDraft((prev) => ({
            ...prev,
            finalCta: { ...prev.finalCta, bgImageUrl: v },
          })),
        onUploadFile: (f) =>
          uploadAndSet("finalCtaBg", f, (u) =>
            setHomeDraft((prev) => ({
              ...prev,
              finalCta: { ...prev.finalCta, bgImageUrl: u },
            }))
          ),
        helpText: "Home bottom strip",
      })}
    </div>
  );
}
